/**
 * Button density / geometry — the shape axis.
 *
 * Visual heights: xs 24 / sm 32 / md 40 / lg 56. Icon md = M3 40dp;
 * touch expands to ≥48dp via HIT_TARGET_MIN. Color lives in Button.color.ts.
 */

import type { ButtonSize, ButtonType } from './Button.types';

/**
 * Trailing icon plate (`rightIconWithBackground`).
 * Plate diameter = button height − 2×inset. xs–md: 4px inset; lg: 8px (Figma hero CTA).
 */
export const TRAILING_PLATE_INSET_PX: Record<ButtonSize, number> = {
  xs: 4,
  sm: 4,
  md: 4,
  lg: 8,
};

export const BUTTON_RIGHT_ICON_PLATE: Record<ButtonSize, string> = {
  xs: 'h-4 w-4',
  sm: 'h-6 w-6',
  md: 'h-8 w-8',
  lg: 'h-10 w-10',
};

/**
 * Glyph size for `icon` / `rightIcon` — owned by Button `size`, not call-site classes.
 * Callers should pass bare icons (e.g. `<Trash2 />`); optional icon className is for
 * non-size concerns (rotate, strokeWidth extras).
 */
export const BUTTON_ICON_GLYPH_CLASS: Record<ButtonSize, string> = {
  xs: 'h-3.5 w-3.5',
  sm: 'h-4 w-4',
  md: 'h-4.5 w-4.5',
  lg: 'h-5.5 w-5.5',
};

/** Loading spinner dimension per size token. */
export const BUTTON_SPINNER_SIZE_CLASS: Record<ButtonSize, string> = {
  xs: 'h-3 w-3 border',
  sm: 'h-3.5 w-3.5 border-2',
  md: 'h-4 w-4 border-2',
  lg: 'h-5 w-5 border-2',
};

export const SIZE_STYLES: Record<ButtonSize, string> = {
  xs: 'text-xs leading-none font-semibold gap-1',
  sm: 'text-sm leading-none font-semibold gap-1.5',
  md: 'text-md leading-5 font-semibold gap-2',
  lg: 'text-lg leading-5 font-semibold gap-4',
};

/** Visual box (not touch target). Touch expands via HIT_TARGET_MIN. */
export const PADDING: Record<ButtonSize, string> = {
  xs: 'h-6 px-3',
  sm: 'h-8 px-4',
  md: 'h-10 px-5',
  lg: 'h-14 px-7',
};

/**
 * Contained + trailing plate — explicit pl/pr (never px-* + pl/pr conflict).
 * Right padding matches vertical plate inset; left matches standard label inset.
 */
export const PADDING_WITH_TRAILING_PLATE: Record<ButtonSize, string> = {
  xs: 'h-6 pl-3 pr-1',
  sm: 'h-8 pl-4 pr-1',
  md: 'h-10 pl-5 pr-1',
  lg: 'h-14 pl-6 pr-2',
};

/** Typography + label↔plate gap when the trailing plate is present. */
export const SIZE_STYLES_WITH_TRAILING_PLATE: Record<ButtonSize, string> = {
  xs: 'text-xs leading-none font-semibold gap-1',
  sm: 'text-sm leading-none font-semibold gap-1.5',
  md: 'text-md leading-5 font-semibold gap-2',
  lg: 'text-lg leading-5 font-semibold gap-3',
};

/** Icon-only visual box — square; md = M3 40dp. */
export const PADDING_ICON: Record<ButtonSize, string> = {
  xs: 'h-6 w-6 p-0',
  sm: 'h-8 w-8 p-0',
  md: 'h-10 w-10 p-0',
  lg: 'h-14 w-14 p-0',
};

export const ROUNDED: Record<ButtonType, string> = {
  contained: 'rounded-full',
  outlined: 'rounded-full',
  text: 'rounded-full',
  icon: 'rounded-full',
};

/**
 * M3 minimum touch target 48×48dp via invisible ::before.
 * Visual height may be smaller; hit area grows to at least 48px each axis.
 */
export const HIT_TARGET_MIN =
  'before:absolute before:content-[""] before:left-1/2 before:top-1/2 before:z-0 ' +
  'before:h-full before:min-h-12 before:w-full before:min-w-12 ' +
  'before:-translate-x-1/2 before:-translate-y-1/2';
